import React, { useState, useEffect, useCallback } from 'react';
import { Plus, FileText, Send, Eye, Users, Euro, CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  'brouillon': 'bg-gray-100 text-gray-800',
  'emis': 'bg-blue-100 text-blue-800',
  'partiellement_paye': 'bg-yellow-100 text-yellow-800',
  'paye': 'bg-green-100 text-green-800',
  'en_retard': 'bg-red-100 text-red-800'
};

const statusLabels = {
  'brouillon': 'Brouillon',
  'emis': 'Émis',
  'partiellement_paye': 'Partiellement payé',
  'paye': 'Payé',
  'en_retard': 'En retard'
};

export default function AppelsFondsSection({ buildingId, exerciceId, token }) {
  const [appels, setAppels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAppel, setSelectedAppel] = useState(null);

  const fetchAppels = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/comptabilite/appels-fonds?building_id=${buildingId}&exercice_id=${exerciceId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAppels(data);
      }
    } catch (error) {
      console.error('Error fetching appels:', error);
    } finally {
      setLoading(false);
    }
  }, [buildingId, exerciceId, token]);

  useEffect(() => {
    fetchAppels();
  }, [fetchAppels]);

  const handleEmettre = async (appelId) => {
    try {
      const response = await fetch(`${API_URL}/api/comptabilite/appels-fonds/${appelId}/emettre`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Appel de fonds émis et envoyé aux coproprietaires');
        fetchAppels();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'émission');
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const totalAppele = appels.reduce((sum, a) => sum + (a.montant_total || 0), 0);
  const totalEncaisse = appels.reduce((sum, a) => sum + (a.montant_encaisse || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Appels de fonds</h2>
          <p className="text-sm text-gray-500">
            {appels.length} appel(s) • Appelé: {formatMoney(totalAppele)} • Encaissé: {formatMoney(totalEncaisse)}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA6]"
        >
          <Plus className="h-5 w-5" />
          Nouvel appel de fonds
        </button>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div>
          </div>
        ) : appels.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucun appel de fonds</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {appels.map(appel => (
              <div key={appel.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-medium text-gray-900">{appel.numero}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[appel.statut]}`}>
                        {statusLabels[appel.statut]}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{appel.libelle}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {appel.lignes?.length || 0} coproprietaires
                      </span>
                      <span className="flex items-center gap-1">
                        <Euro className="h-4 w-4" />
                        {formatMoney(appel.montant_total)}
                      </span>
                      <span>
                        Échéance: {appel.date_echeance ? new Date(appel.date_echeance).toLocaleDateString('fr-FR') : '-'}
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="w-32 mr-4">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Encaissé</span>
                      <span>{Math.round((appel.montant_encaisse / appel.montant_total) * 100) || 0}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 rounded-full"
                        style={{ width: `${Math.min(100, (appel.montant_encaisse / appel.montant_total) * 100) || 0}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {appel.statut === 'brouillon' && (
                      <button
                        onClick={() => handleEmettre(appel.id)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                      >
                        <Send className="h-4 w-4" />
                        Émettre
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedAppel(appel.id)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateAppelModal
          buildingId={buildingId}
          exerciceId={exerciceId}
          token={token}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchAppels();
          }}
        />
      )}

      {/* Detail Modal */}
      {selectedAppel && (
        <AppelDetailModal
          appelId={selectedAppel}
          token={token}
          onClose={() => setSelectedAppel(null)}
          onPaiementAdded={fetchAppels}
        />
      )}
    </div>
  );
}

// Create Appel Modal
function CreateAppelModal({ buildingId, exerciceId, token, onClose, onCreated }) {
  const [formData, setFormData] = useState({
    montant_total: '',
    libelle: '',
    type_charges: 'provisions',
    frequence: 'trimestriel',
    date_echeance: '',
    periode_debut: '',
    periode_fin: ''
  });
  const [saving, setSaving] = useState(false);

  // Set default dates
  useEffect(() => {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3);
    const startMonth = quarter * 3;
    const periodeDebut = new Date(now.getFullYear(), startMonth, 1);
    const periodeFin = new Date(now.getFullYear(), startMonth + 3, 0);
    const echeance = new Date(now.getFullYear(), startMonth + 1, 15);

    setFormData(prev => ({
      ...prev,
      periode_debut: periodeDebut.toISOString().split('T')[0],
      periode_fin: periodeFin.toISOString().split('T')[0],
      date_echeance: echeance.toISOString().split('T')[0],
      libelle: `Appel de fonds T${quarter + 1} ${now.getFullYear()}`
    }));
  }, []);

  const handleSubmit = async () => {
    if (!formData.montant_total || parseFloat(formData.montant_total) <= 0) {
      toast.error('Montant invalide');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/comptabilite/appels-fonds`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          building_id: buildingId,
          exercice_id: exerciceId,
          montant_total: parseFloat(formData.montant_total),
          ...formData
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Appel de fonds créé pour ${data.nb_coproprietaires} coproprietaires`);
        onCreated();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold">Nouvel appel de fonds</h3>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Montant total à appeler *</label>
            <div className="relative">
              <input
                type="number"
                value={formData.montant_total}
                onChange={(e) => setFormData({ ...formData, montant_total: e.target.value })}
                className="w-full px-4 py-2 pr-12 border border-gray-200 rounded-lg text-lg"
                placeholder="5000"
                min="0"
                step="100"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">€</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Ce montant sera réparti entre les coproprietaires selon leurs tantièmes
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Libellé</label>
            <input
              type="text"
              value={formData.libelle}
              onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={formData.type_charges}
                onChange={(e) => setFormData({ ...formData, type_charges: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg"
              >
                <option value="provisions">Provisions</option>
                <option value="regularisation">Régularisation</option>
                <option value="travaux">Travaux</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fréquence</label>
              <select
                value={formData.frequence}
                onChange={(e) => setFormData({ ...formData, frequence: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg"
              >
                <option value="mensuel">Mensuel</option>
                <option value="trimestriel">Trimestriel</option>
                <option value="semestriel">Semestriel</option>
                <option value="annuel">Annuel</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Période du</label>
              <input
                type="date"
                value={formData.periode_debut}
                onChange={(e) => setFormData({ ...formData, periode_debut: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Au</label>
              <input
                type="date"
                value={formData.periode_fin}
                onChange={(e) => setFormData({ ...formData, periode_fin: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date d'échéance</label>
            <input
              type="date"
              value={formData.date_echeance}
              onChange={(e) => setFormData({ ...formData, date_echeance: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            />
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-6 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA6] disabled:opacity-50"
          >
            {saving ? 'Création...' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Appel Detail Modal
function AppelDetailModal({ appelId, token, onClose, onPaiementAdded }) {
  const [appel, setAppel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingOnline, setPayingOnline] = useState(false);

  useEffect(() => {
    const fetchAppel = async () => {
      try {
        const response = await fetch(`${API_URL}/api/comptabilite/appels-fonds/${appelId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          setAppel(await response.json());
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAppel();
  }, [appelId, token]);

  // Check for Stripe redirect on mount
  useEffect(() => {
    const checkPaymentStatus = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      const paymentStatus = urlParams.get('payment');
      
      if (sessionId && paymentStatus === 'success') {
        // Poll for payment status
        try {
          const response = await fetch(`${API_URL}/api/comptabilite/stripe/status/${sessionId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.payment_status === 'paid') {
              toast.success('Paiement effectué avec succès !');
              onPaiementAdded();
            }
          }
        } catch (error) {
          console.error('Error checking payment:', error);
        }
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
      }
    };
    checkPaymentStatus();
  }, [token, onPaiementAdded]);

  const handlePayOnline = async (ligneAppelId, montantRestant) => {
    if (montantRestant <= 0) {
      toast.error('Cet appel de fonds est déjà entièrement payé');
      return;
    }

    setPayingOnline(true);
    try {
      const response = await fetch(`${API_URL}/api/comptabilite/stripe/create-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          appel_fonds_id: ligneAppelId,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création du paiement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setPayingOnline(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold">Appel de fonds {appel?.numero}</h3>
          <p className="text-sm text-gray-500">{appel?.libelle}</p>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Montant appelé</p>
              <p className="text-xl font-bold">{formatMoney(appel?.montant_total)}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-green-600">Encaissé</p>
              <p className="text-xl font-bold text-green-600">{formatMoney(appel?.montant_encaisse)}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm text-red-600">Reste à recouvrer</p>
              <p className="text-xl font-bold text-red-600">{formatMoney(appel?.solde_a_recouvrer)}</p>
            </div>
          </div>

          {/* Lignes */}
          <h4 className="font-medium mb-3">Détail par coproprietaire</h4>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Copropriétaire</th>
                <th className="px-3 py-2 text-right">Tantièmes</th>
                <th className="px-3 py-2 text-right">Appelé</th>
                <th className="px-3 py-2 text-right">Payé</th>
                <th className="px-3 py-2 text-right">Solde</th>
                <th className="px-3 py-2 text-center">Statut</th>
                <th className="px-3 py-2 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {appel?.lignes?.map((ligne, i) => (
                <tr key={i} className={ligne.solde_du > 0 ? 'bg-red-50/50' : ''}>
                  <td className="px-3 py-2">{ligne.coproprietaire_nom}</td>
                  <td className="px-3 py-2 text-right">{ligne.tantiemes}</td>
                  <td className="px-3 py-2 text-right">{formatMoney(ligne.montant_appele)}</td>
                  <td className="px-3 py-2 text-right text-green-600">{formatMoney(ligne.montant_paye)}</td>
                  <td className="px-3 py-2 text-right text-red-600">{formatMoney(ligne.solde_du)}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${statusColors[ligne.statut]}`}>
                      {statusLabels[ligne.statut]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {ligne.solde_du > 0 && (
                      <button
                        onClick={() => handlePayOnline(ligne.appel_ligne_id || appelId, ligne.solde_du)}
                        disabled={payingOnline}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 disabled:opacity-50 mx-auto"
                        data-testid={`pay-online-btn-${i}`}
                      >
                        {payingOnline ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <CreditCard className="h-3 w-3" />
                        )}
                        Payer
                      </button>
                    )}
                    {ligne.solde_du <= 0 && (
                      <span className="text-green-600 text-xs">✓ Payé</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-6 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
